import { ClipboardList } from "lucide-react";

const TYPE_COLORS = {
  trailer_40ft: "#2563EB",
  semi_bed_40ft: "#EF4444",
  hydraulic_axle: "#A855F7",
  truck_32ft: "#22C55E",
};

function typeColor(vehicleType) {
  return TYPE_COLORS[vehicleType] || "#3B82F6"; // truck_20ft fallback
}

function CaseNumbers({ loadedCases }) {
  if (!loadedCases.length) return <>—</>;
  return (
    <>
      {loadedCases.map((lc, ci) => (
        <span key={`${lc.case.case_no ?? "n"}-${lc.unit_index ?? ci}`}>
          {ci > 0 ? ", " : ""}
          <span className={lc.is_odc ? "text-[#F97316]" : ""}>
            {lc.case.case_no || "?"}
            {lc.is_odc ? "*" : ""}
          </span>
        </span>
      ))}
    </>
  );
}

function AllocationRow({ alloc, index }) {
  return (
    <tr
      className="border-b border-slate-100 hover:bg-white/[0.03] transition-colors duration-150"
      data-testid={`plan-row-${index}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-6" style={{ background: typeColor(alloc.vehicle_type) }} />
          <span className="mono font-bold text-[#2563EB]">#{index + 1}</span>
        </div>
      </td>
      <td className="px-4 py-3 mono text-sm">
        {alloc.vehicle_name}
        {alloc.has_odc && (
          <span
            className="ml-2 mono text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 border border-[#F97316] text-[#F97316] rounded-none"
            data-testid={`odc-badge-${index}`}
          >
            ODC · RTO Permit
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="mono font-semibold text-white text-base">
          <CaseNumbers loadedCases={alloc.loaded_cases} />
        </span>
      </td>
      <td className="px-4 py-3 mono text-right text-[#64748B]">
        {alloc.loaded_cases.length}
      </td>
      <td className="px-4 py-3 mono text-right">
        <span className="text-white">{alloc.used_weight_kg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        <span className="text-[#64748B]"> / {alloc.max_payload_kg.toLocaleString()}</span>
      </td>
    </tr>
  );
}

function UnassignedRow({ unassigned }) {
  return (
    <tr className="border-b border-slate-100 bg-[#EF4444]/5">
      <td className="px-4 py-3">
        <span className="mono font-bold text-[#EF4444]">!</span>
      </td>
      <td className="px-4 py-3 mono text-sm text-[#EF4444]">UNASSIGNED</td>
      <td className="px-4 py-3">
        <span className="mono font-semibold text-[#EF4444] text-base">
          {unassigned.map((u) => u.case_no || "?").join(", ")}
        </span>
      </td>
      <td className="px-4 py-3 mono text-right text-[#EF4444]">
        {unassigned.length}
      </td>
      <td className="px-4 py-3 mono text-right text-[#EF4444]">—</td>
    </tr>
  );
}

export default function LoadingPlanTable({ result }) {
  return (
    <div className="bg-[#F8FAFC] border-2 border-[#2563EB]/40" data-testid="loading-plan-summary">
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-9 h-9 bg-[#2563EB] flex items-center justify-center">
          <ClipboardList className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="overline">Loading Plan</div>
          <div className="heading text-lg font-semibold">Trailer / Truck-wise Case Assignment</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFFFF]">
            <tr className="border-b border-slate-200">
              <th className="text-left px-4 py-3 overline w-[100px]">Vehicle</th>
              <th className="text-left px-4 py-3 overline">Type</th>
              <th className="text-left px-4 py-3 overline">Cases to Load</th>
              <th className="text-right px-4 py-3 overline w-[100px]">Count</th>
              <th className="text-right px-4 py-3 overline w-[180px]">Weight (kg)</th>
            </tr>
          </thead>
          <tbody>
            {result.allocations.map((a, i) => (
              <AllocationRow key={a.id || i} alloc={a} index={i} />
            ))}
            {result.unassigned?.length > 0 && (
              <UnassignedRow unassigned={result.unassigned} />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
